import React, { useEffect, useState } from 'react';
import EntryListPage from './components/EntryListPage';
import arrayToBreadCrumbs from '../../utils/sms/arrayToBreadCrumbs';
import EntryModal from './components/EntryModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEntryRegisteredVehicles,
  registerVehicleEntry,
  registerVehicleExit,
} from '../../parse-functions/vehicleEntries';
import { updateSpinnerState } from '../../actions/spinner';
import { updateToastInfo } from '../../actions/settings';
import { useTranslation } from 'react-i18next';
import { vehicleColumns } from './utils';
import ConfirmationModal from '../../components/common/confirmationModal';
import { getVendorsListInRegion } from '../../parse-functions/vendors';
import { setVendorsListInStore } from '../../actions/vendors';
import { convertArrayToObj } from '../../utils/json';

const EntryList = () => {
  const { t } = useTranslation();
  const zone = useSelector((state) => state?.user?.warehouse?.zone || '');
  const storeName = useSelector(
    (state) => state?.user?.warehouse?.warehouseName || ''
  );
  const guardObjectId = useSelector(
    (state) => state?.user?.user?.objectId || ''
  );
  const dispatch = useDispatch();
  const [entryModal, setEntryModal] = useState(false);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState([]);
  const breadCrumbItems = arrayToBreadCrumbs([['Entry'], ['List']]);
  const [showModal, setShowModal] = useState(false);
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const bulkActionOptions = [
    {
      label: 'Mark Exit',
      value: 'markExit',
    },
  ];
  const [vendorOptions, setVendorOptions] = useState([]);
  const handleShowEntryModal = () => {
    setEntryModal(true);
  };
  const vendorsList = useSelector((state) => state?.vendors?.vendorsList || {});

  const fetchData = async () => {
    try {
      dispatch(updateSpinnerState(true));
      const res = await getEntryRegisteredVehicles({ storeName, isAdmin });
      console.log(res);
      setVehiclesData(res);
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t(''),
          message: t('Vehicles fetched successfully'),
        })
      );
    } catch (error) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('failed to fetch Vehicles'),
          message: t(`${error.message}`),
        })
      );
    }
  };
  useEffect(() => {
    if (storeName) {
      fetchData();
    }
  }, [storeName]);

  const hanldeSubmit = async (data) => {
    const finalData = { ...data, guardObjectId };
    try {
      dispatch(updateSpinnerState(true));
      const entry = await registerVehicleEntry(finalData);
      await fetchData();
      setEntryModal(false);
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t('vehicle Entry register successfully'),
          message: t(`${entry.objectId} registered`),
        })
      );
    } catch (error) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('vehicle Entry register failed'),
          message: t(`${error.message}`),
        })
      );
      throw error;
    }
  };

  const onRowSelect = (selected, objectId) => {
    if (selected) {
      setSelectedVehicle((prev) => {
        const selectedItem = vehiclesData.find(
          (item) => item.objectId === objectId
        );
        return [...prev, selectedItem];
      });
    } else {
      setSelectedVehicle((prev) =>
        prev.filter((item) => item.objectId !== objectId)
      );
    }
    setVehiclesData((prev) =>
      prev.map((item) =>
        item.objectId === objectId ? { ...item, isSelected: selected } : item
      )
    );
  };

  const handleSelectedRowsUpdate = (actionId) => {
    if (selectedVehicle.length > 1) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t(''),
          message: t('Only One Vehicle Allowed To Update'),
        })
      );
      return;
    }
    switch (actionId) {
      case 'markExit': {
        handleShowModal();
        break;
      }
      default:
        break;
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleModalOnConfirmClick = async () => {
    try {
      dispatch(updateSpinnerState(true));
      const entry = await registerVehicleExit({
        entryId: selectedVehicle[0].objectId,
      });
      await fetchData();
      setShowModal(false);
      setSelectedVehicle([]);
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t(''),
          message: t(`vehicle Exit successfully`),
        })
      );
    } catch (error) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('vehicle exit failed'),
          message: t(`${error.message}`),
        })
      );
    }
  };

  useEffect(() => {
    const fetchVendor = async () => {
      if (Object.keys(vendorsList).length > 0) {
        const vendorObj = {};
        Object.values(vendorsList).forEach(
          (item) => (vendorObj[item['vendorName']] = item['objectId'])
        );
        setVendorOptions(vendorObj);
        return;
      }
      dispatch(updateSpinnerState(true));
      try {
        const filters = {
          status: 'active',
          getAllVendors: true,
        };
        let vendors = await getVendorsListInRegion(filters);
        dispatch(
          setVendorsListInStore(
            convertArrayToObj(JSON.parse(JSON.stringify(vendors)), 'objectId')
          )
        );
        vendors = JSON.parse(JSON.stringify(vendors));
        const vendorObj = vendors?.reduce((acc, row) => {
          acc[row['vendorName']] = row['objectId'];
          return acc;
        }, {});
        setVendorOptions(vendorObj);
        dispatch(updateSpinnerState(false));
      } catch (e) {
        dispatch(updateSpinnerState(false));
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: t('Error'),
            message: t(e.message),
          })
        );
      }
    };
    fetchVendor();
  }, []);

  return (
    <>
      <EntryListPage
        title={'Entry List'}
        data={vehiclesData}
        columns={vehicleColumns(onRowSelect)}
        breadCrumbItems={breadCrumbItems}
        handleEntryModal={handleShowEntryModal}
        enableBulkActions={selectedVehicle.length > 0}
        hideBulkActions={!bulkActionOptions}
        bulkActionOptions={bulkActionOptions}
        handleItemBulkUpdate={handleSelectedRowsUpdate}
        handleRefresh={handleRefresh}
      />

      <EntryModal
        showModal={entryModal}
        closeModal={() => setEntryModal(false)}
        handleSubmit={hanldeSubmit}
        zone={zone}
        vendorOptions={vendorOptions}
      />

      <ConfirmationModal
        showModal={showModal}
        onConfirm={handleModalOnConfirmClick}
        closeModal={() => {
          setShowModal(false);
        }}
        modalBody={t(
          `Do You Want To Mark This ${selectedVehicle[0]?.vehicleNumber} Vehicle as Exited!`
        )}
        modalTitle={t(`Exit Confirmation`)}
      />
    </>
  );
};

export default EntryList;
